/**
 * Hook for fetching persona statistics and data
 */

import { useState, useEffect, useCallback } from 'react';
import {
  personaService,
  PersonaService,
  PersonaStats,
  PersonaData,
} from '../services/personaService';

export function usePersonaStats(service: PersonaService = personaService) {
  const [stats, setStats] = useState<PersonaStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const data = await service.getStats();
      setStats(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching persona stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats,
  };
}

export function usePersonaList(
  filters?: {
    segment?: string;
    status?: string;
    limit?: number;
  },
  service: PersonaService = personaService
) {
  const [personas, setPersonas] = useState<PersonaData[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPersonas = useCallback(async () => {
    try {
      setLoading(true);
      const data = await service.listPersonas(filters);
      setPersonas(data.personas);
      setTotal(data.total);
      setError(null);
    } catch (err) {
      console.error('Error fetching personas:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch personas');
    } finally {
      setLoading(false);
    }
  }, [filters, service]);

  useEffect(() => {
    fetchPersonas();
  }, [fetchPersonas]);

  return {
    personas,
    total,
    loading,
    error,
    refetch: fetchPersonas,
  };
}
